/**
 * Unified formatting utilities for consistent display across the application
 */

/**
 * Formats file size in human readable format
 * Consolidates multiple implementations across the codebase
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 B";
  }

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Use 1 decimal place for consistency and readability
  const value = Number.parseFloat((bytes / k ** i).toFixed(1));
  return `${value} ${sizes[i]}`;
}

/**
 * Formats duration in human readable format
 * Consolidates multiple implementations across trace and deployment components
 */
export function formatDuration(duration: number): string {
  if (duration < 1000) {
    return `${duration}ms`;
  }
  return `${(duration / 1000).toFixed(1)}s`;
}

/**
 * Formats timestamp for trace displays with high precision
 */
export function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });
}

/**
 * Formats date for user-friendly display
 */
export function formatDate(date: string | number | Date): string {
  const dateObj = new Date(date);
  return `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString()}`;
}

/**
 * Formats date to ISO string for API usage
 */
export function formatDateISO(date: string | Date): string {
  return new Date(date).toISOString();
}

/**
 * Formats time only for chat displays
 */
export function formatTime(date: Date | undefined): string {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
